import { BankStatementsState } from "./reducer";
import {
  BankStatementChecklistStatus,
  PulseBankAccount,
  PulseDocSummary,
} from "./types";
import {
  getTransactionConsentKey,
  getVisibleRangeErrors,
  isAccountFailed,
  isActivePipelineStatus,
  isDocumentAwaitingSubmission,
  isDocumentFailed,
  isDocumentNotSubmitted,
  isDocumentPasswordRequired,
  isDocumentSubmittable,
  isIdentificationFailure,
  isUnresolvedDocumentProcessing,
} from "./utils";

export const getUnsubmittedDocIds = (source: {
  pendingIdentification?: PulseDocSummary[];
  accounts?: PulseBankAccount[];
}) =>
  Array.from(
    new Set(
      [
        ...(source.pendingIdentification || []),
        ...(source.accounts || []).flatMap((account) => account.documents || []),
      ]
        .filter(isDocumentSubmittable)
        .map((doc) => doc.id)
    )
  );

export interface BankStatementsFlowFlags {
  isFirstTimeReview: boolean;
  isFirstTime: boolean;
  isReadingStatements: boolean;
  hasLocalWork: boolean;
  hasRequiredPassword: boolean;
  canProceed: boolean;
}

export type BankStatementNavigationWarningKind =
  | "empty"
  | "awaiting_analysis"
  | "needs_attention";

export const getFlowFlags = (state: BankStatementsState): BankStatementsFlowFlags => {
  const hasPendingAwaitingSubmission =
    state.pendingIdentification.some(isDocumentAwaitingSubmission);
  const hasPendingNeedingAction =
    hasPendingAwaitingSubmission ||
    state.pendingIdentification.some(isDocumentPasswordRequired);
  const allPendingFailed =
    !!state.pendingIdentification.length &&
    state.pendingIdentification.every(isIdentificationFailure);
  const isFirstTimeReview =
    !state.accounts.length && hasPendingNeedingAction;
  const isFirstTime =
    state.loaded &&
    !state.accounts.length &&
    (!state.pendingIdentification.length || isFirstTimeReview);
  const isReadingStatements =
    state.loaded &&
    !state.accounts.length &&
    !!state.pendingIdentification.length &&
    !hasPendingNeedingAction &&
    !allPendingFailed &&
    state.pendingIdentification.some(isUnresolvedDocumentProcessing);
  const hasLocalWork = !!state.busyScopes.length;
  const hasRequiredPassword = [
    ...state.pendingIdentification,
    ...state.accounts.flatMap((account) => account.documents),
  ].some(isDocumentPasswordRequired);
  const hasServerSubmission =
    !!state.accounts.length ||
    state.pendingIdentification.some(
      (doc) => !isDocumentAwaitingSubmission(doc)
    );
  const canProceed =
    !hasLocalWork &&
    !hasRequiredPassword &&
    !state.submissionError &&
    (state.submittedInSession || hasServerSubmission);

  return {
    isFirstTimeReview,
    isFirstTime,
    isReadingStatements,
    hasLocalWork,
    hasRequiredPassword,
    canProceed,
  };
};

export const toChecklistValidation = (status: BankStatementChecklistStatus) => ({
  successDetails: { message: status.isError ? "" : status.message },
  errorDetails: { message: status.isError ? status.message : "" },
  isError: status.isError,
  isLoading: status.isLoading,
});

export const getChecklistStatus = (
  state: BankStatementsState,
  isReadingStatements: boolean
): BankStatementChecklistStatus => {
  const isInitialLoading = !state.loaded || state.loading;
  const accountsWithDocuments = state.accounts.filter(
    (account) => account.documents.length > 0
  );
  const accountStatuses = accountsWithDocuments.map(
    (account) => account.pipelineStatus
  );
  const isProcessing =
    isReadingStatements || accountStatuses.some(isActivePipelineStatus);
  const hasPasswordProtected = [
    ...state.pendingIdentification,
    ...accountsWithDocuments.flatMap((account) => account.documents),
  ].some(isDocumentPasswordRequired);
  const hasUnsubmitted = state.pendingIdentification.some(
    isDocumentNotSubmitted
  );
  const hasSubmittedPending = state.pendingIdentification.some(
    isUnresolvedDocumentProcessing
  );
  const processing = isProcessing || hasSubmittedPending;
  const unconfirmedMissingTransactionRangeCount = accountsWithDocuments.reduce(
    (count, account) => {
      const errorDetails = account.lastAnalysis?.errorDetails;
      const { transactionRanges } = getVisibleRangeErrors(errorDetails);
      const consentMap = errorDetails?.noTransactionConsent || {};
      return (
        count +
        transactionRanges.filter(
          (range) => !consentMap[getTransactionConsentKey(range)]?.consented
        ).length
      );
    },
    0
  );
  const requiresMissingTransactionsConsent =
    unconfirmedMissingTransactionRangeCount > 0;
  const hasBalanceReconciliationFailure = accountsWithDocuments.some(
    (account) =>
      getVisibleRangeErrors(account.lastAnalysis?.errorDetails)
        .balanceRanges.length > 0
  );
  const accountFailed = accountsWithDocuments.some(isAccountFailed);
  const hasAnyData = !!state.accounts.length || !!state.pendingIdentification.length;
  const isError =
    state.loaded &&
    (!hasAnyData ||
      hasPasswordProtected ||
      hasUnsubmitted ||
      accountFailed ||
      (!processing &&
        (hasBalanceReconciliationFailure ||
          requiresMissingTransactionsConsent)) ||
      !!state.submissionError);

  let message = `${state.accounts.length} ${
    state.accounts.length === 1 ? "account" : "accounts"
  } added.`;
  if (isInitialLoading) message = "Loading bank statements...";
  else if (!hasAnyData) message = "Missing bank statements.";
  else if (processing && !isError) message = "Processing statements...";
  else if (requiresMissingTransactionsConsent)
    message = "Missing statement periods require confirmation.";
  else if (hasBalanceReconciliationFailure)
    message = "Balance reconciliation failed.";
  else if (hasPasswordProtected)
    message = "Some bank statements require a password.";
  else if (hasUnsubmitted)
    message = "Some statements could not be assigned to a bank account.";
  else if (accountFailed) message = "Statement processing failed.";

  return {
    isLoading: isInitialLoading || (processing && !isError),
    isError,
    message,
    accountCount: state.accounts.length,
    requiresMissingTransactionsConsent,
    hasBalanceReconciliationFailure,
    unconfirmedMissingTransactionRangeCount,
  };
};

export const getBankStatementNavigationWarning = (
  state: BankStatementsState,
  checklistStatus: BankStatementChecklistStatus
): BankStatementNavigationWarningKind | null => {
  if (!state.loaded || state.loading) return null;

  const documents = [
    ...state.pendingIdentification,
    ...state.accounts.flatMap((account) => account.documents),
  ];
  const hasDocumentAttention = documents.some(
    (doc) => isDocumentPasswordRequired(doc) || isDocumentFailed(doc)
  );
  const hasRangeAttention =
    checklistStatus.requiresMissingTransactionsConsent ||
    checklistStatus.hasBalanceReconciliationFailure;
  const hasServerProcessing =
    state.accounts.some((account) =>
      isActivePipelineStatus(account.pipelineStatus)
    ) || documents.some(isUnresolvedDocumentProcessing);

  if (
    state.busyScopes.length > 0 ||
    hasDocumentAttention ||
    !!state.submissionError
  ) {
    return "needs_attention";
  }

  if (!documents.length) return "empty";

  if (!state.accounts.length) {
    if (getUnsubmittedDocIds(state).length > 0) return "awaiting_analysis";
    return null;
  }

  if (hasServerProcessing) return null;
  if (hasRangeAttention) return "needs_attention";

  return null;
};
